import type { SupabaseClient } from '@supabase/supabase-js';
import { InfrastructureError } from '@dma/shared';
import { Banner, BANNER_ID } from '../domain/entities/Banner.entity';
import type { BannerRepository } from '../domain/repositories/BannerRepository.interface';

const TABLE_NAME = 'site_banner';

interface BannerRow {
  id: string;
  image_url: string | null;
  alt_text: string;
  is_active: boolean;
  updated_at: string;
}

function rowToEntity(row: BannerRow): Banner {
  return Banner.create({
    id: row.id,
    imageUrl: row.image_url,
    altText: row.alt_text,
    isActive: row.is_active,
    updatedAt: row.updated_at
  });
}

/**
 * Infrastructure implementation backed by the `site_banner` table (see
 * infra/supabase/migrations/005_create_site_banner.sql). Always addresses
 * the single row by BANNER_ID — there is no list/getAll here because a
 * banner is a site setting, not a content collection.
 */
export class SupabaseBannerRepository implements BannerRepository {
  constructor(private readonly client: SupabaseClient) {}

  public async getActive(): Promise<Banner | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('id', BANNER_ID)
      .eq('is_active', true)
      .maybeSingle<BannerRow>();

    if (error) {
      throw new InfrastructureError('BANNER_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async getForAdmin(): Promise<Banner | null> {
    const { data, error } = await this.client
      .from(TABLE_NAME)
      .select('*')
      .eq('id', BANNER_ID)
      .maybeSingle<BannerRow>();

    if (error) {
      throw new InfrastructureError('BANNER_FETCH_FAILED', error.message, { cause: error });
    }
    return data ? rowToEntity(data) : null;
  }

  public async save(entity: Banner): Promise<void> {
    const { props } = entity;
    const { error } = await this.client.from(TABLE_NAME).upsert({
      id: props.id,
      image_url: props.imageUrl,
      alt_text: props.altText,
      is_active: props.isActive,
      updated_at: props.updatedAt
    });
    if (error) {
      throw new InfrastructureError('BANNER_SAVE_FAILED', error.message, { cause: error });
    }
  }
}
